import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"

type FairUseDenyDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (reason: string) => void
  isPending: boolean
  userLabel: string
  motivation?: string | null
}

/**
 * Turning a request down.
 *
 * Approving is inline on the row — the common, cheap answer. Denying is not:
 * the person asked for something and gets nothing, so the reason is REQUIRED
 * here (the API accepts an empty one; the UI does not) and the confirm button
 * stays disabled until there is something to tell them.
 */
export function FairUseDenyDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  userLabel,
  motivation,
}: FairUseDenyDialogProps) {
  const { t } = useTranslation()
  const [reason, setReason] = useState("")

  useEffect(() => {
    if (open) setReason("")
  }, [open])

  const trimmed = reason.trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.fairUse.ops.denyTitle", "Turn down {{user}}'s request", {
              user: userLabel,
            })}
          </DialogTitle>
          <DialogDescription>
            {t(
              "adminDashboard.fairUse.ops.denyDescription",
              "They keep their current monthly budget. Your reason is shown to them."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {motivation && (
            <blockquote className="text-muted-foreground border-warning border-l-2 pl-3 text-sm italic">
              {motivation}
            </blockquote>
          )}
          <div className="space-y-2">
            <Label htmlFor="fair-use-deny-reason">
              {t("adminDashboard.fairUse.ops.denyReasonLabel", "Reason")}
            </Label>
            <Textarea
              id="fair-use-deny-reason"
              value={reason}
              placeholder={t(
                "adminDashboard.fairUse.ops.denyReasonPlaceholder",
                "Tell them why, so they know what to do next."
              )}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button className="h-10" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            className="h-10"
            disabled={isPending || !trimmed}
            variant="destructive"
            onClick={() => onSubmit(trimmed)}
          >
            {isPending ? <Spinner className="mr-2 size-4" /> : null}
            {t("adminDashboard.fairUse.ops.denyConfirm", "Turn down request")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
